/**
 * Cura — Product Reels Carousel JS
 * Clean rewrite: proper infinite loop, drag/swipe, modal with video controls
 */
(function () {
  'use strict';

  /* ============================================================
     CAROUSEL
  ============================================================ */
  class Carousel {
    constructor(el) {
      this.root     = el;
      this.track    = el.querySelector('[data-track]');
      this.wrap     = el.querySelector('[data-track-wrap]');
      this.prevBtn  = el.querySelector('[data-prev]');
      this.nextBtn  = el.querySelector('[data-next]');
      this.dotsWrap = el.querySelector('[data-dots]');
      this.origCards = Array.from(this.track.querySelectorAll('[data-card]'));

      if (!this.track || this.origCards.length === 0) return;

      this.gap      = 16; // px — matches CSS gap 1.6rem at 10px base
      this.index    = 0;  // logical index in origCards
      this.offset   = 0;  // DOM position
      this.dragging = false;
      this.startX   = 0;
      this.dragDx   = 0;
      this.timer    = null;

      const section = this.root.closest('[data-section-id]');
      this.doAutoplay = section ? section.dataset.autoplay === 'true' : false;
      this.autoSpeed  = parseInt((section ? section.dataset.autoplaySpeed : '4'), 10) * 1000;

      this._build();
      this._bind();
      this._goTo(0, false);
      if (this.doAutoplay) this._startTimer();
    }

    // ---- How many cards visible based on viewport ----
    _visible() {
      const w = this.wrap.offsetWidth;
      const cw = this._cardW();
      return Math.max(1, Math.floor((w + this.gap) / (cw + this.gap)));
    }

    _cardW() {
      return this.origCards[0] ? this.origCards[0].offsetWidth : 220;
    }

    // ---- Clone cards for infinite loop ----
    _build() {
      const n = this.origCards.length;
      const cloneCount = Math.min(n, 4);

      // Remove old clones
      this.track.querySelectorAll('[data-clone]').forEach(c => c.remove());

      // Prepend last N clones
      const before = this.origCards.slice(-cloneCount).map(c => {
        const cl = c.cloneNode(true);
        cl.dataset.clone = 'true';
        cl.setAttribute('aria-hidden', 'true');
        cl.removeAttribute('tabindex');
        return cl;
      });
      before.reverse().forEach(cl => this.track.insertBefore(cl, this.track.firstChild));

      // Append first N clones
      const after = this.origCards.slice(0, cloneCount).map(c => {
        const cl = c.cloneNode(true);
        cl.dataset.clone = 'true';
        cl.setAttribute('aria-hidden', 'true');
        cl.removeAttribute('tabindex');
        return cl;
      });
      after.forEach(cl => this.track.appendChild(cl));

      this.clonesBefore = cloneCount;
      this.allCards = Array.from(this.track.querySelectorAll('[data-card]'));
    }

    // ---- Pixel offset for a DOM position ----
    _posX(pos) {
      return pos * (this._cardW() + this.gap);
    }

    // ---- Go to logical index (with animation) ----
    _goTo(idx, animate = true) {
      const n = this.origCards.length;
      this.index = ((idx % n) + n) % n;
      this.offset = this.clonesBefore + this.index;

      if (!animate) {
        this.track.style.transition = 'none';
        requestAnimationFrame(() => {
          this.track.style.transform = `translateX(-${this._posX(this.offset)}px)`;
          requestAnimationFrame(() => { this.track.style.transition = ''; });
        });
      } else {
        this.track.style.transform = `translateX(-${this._posX(this.offset)}px)`;
      }

      this._updateDots();
    }

    // ---- Slide by delta (prev/next) ----
    _slide(dir) {
      this._stopTimer();
      const n = this.origCards.length;
      const nextOffset = this.offset + dir;
      this.offset = nextOffset;
      this.track.style.transform = `translateX(-${this._posX(nextOffset)}px)`;

      this.track.addEventListener('transitionend', () => {
        // Silently jump if we're in clone territory
        if (this.offset >= this.clonesBefore + n) {
          this.index = 0;
          this.offset = this.clonesBefore;
          this._goTo(this.index, false);
        } else if (this.offset < this.clonesBefore) {
          this.index = n - 1;
          this.offset = this.clonesBefore + n - 1;
          this._goTo(this.index, false);
        } else {
          this.index = this.offset - this.clonesBefore;
          this._updateDots();
        }
      }, { once: true });

      if (this.doAutoplay) this._startTimer();
    }

    _updateDots() {
      if (!this.dotsWrap) return;
      this.dotsWrap.querySelectorAll('[data-dot]').forEach((d, i) => {
        d.classList.toggle('is-active', i === this.index);
      });
    }

    _startTimer() {
      this._stopTimer();
      this.timer = setInterval(() => this._slide(1), this.autoSpeed);
    }

    _stopTimer() { clearInterval(this.timer); }

    _bind() {
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this._slide(-1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this._slide(1));

      if (this.dotsWrap) {
        this.dotsWrap.querySelectorAll('[data-dot]').forEach((dot) => {
          dot.addEventListener('click', () => this._goTo(parseInt(dot.dataset.dot, 10)));
        });
      }

      // Keyboard
      this.root.addEventListener('keydown', e => {
        if (e.key === 'ArrowLeft')  { e.preventDefault(); this._slide(-1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); this._slide(1); }
      });

      // Drag / swipe
      const track = this.track;
      let baseX = 0;

      const onStart = (x) => {
        this.dragging = true;
        this.wasDrag  = false;
        this.startX   = x;
        this.dragDx   = 0;
        baseX = this._posX(this.offset);
        track.classList.add('is-dragging');
        this._stopTimer();
      };

      const onMove = (x) => {
        if (!this.dragging) return;
        this.dragDx = x - this.startX;
        if (Math.abs(this.dragDx) > 6) this.wasDrag = true;
        track.style.transform = `translateX(${-(baseX - this.dragDx)}px)`;
      };

      const onEnd = () => {
        if (!this.dragging) return;
        this.dragging = false;
        track.classList.remove('is-dragging');
        track.style.transition = '';
        if (this.dragDx < -60)     this._slide(1);
        else if (this.dragDx > 60) this._slide(-1);
        else this._goTo(this.index);
        if (this.doAutoplay) this._startTimer();
      };

      track.addEventListener('mousedown',  e => onStart(e.clientX));
      track.addEventListener('touchstart', e => onStart(e.touches[0].clientX), { passive: true });
      window.addEventListener('mousemove',  e => onMove(e.clientX));
      window.addEventListener('touchmove',  e => onMove(e.touches[0].clientX), { passive: true });
      window.addEventListener('mouseup',  onEnd);
      window.addEventListener('touchend', onEnd);

      // Card click → modal (only non-clone cards)
      this.origCards.forEach(card => {
        card.addEventListener('click', () => {
          if (!this.wasDrag) openModal(card);
        });
        card.addEventListener('keydown', e => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openModal(card); }
        });
      });

      // Rebuild on resize
      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          this._build();
          this._goTo(this.index, false);
        }, 200);
      });
    }
  }

  /* ============================================================
     MODAL
  ============================================================ */
  const modal   = document.getElementById('cura-rmodal');
  const vid     = document.getElementById('cura-rmodal-video');
  const playBtn = document.getElementById('cura-rplay');
  const muteBtn = document.getElementById('cura-rmute');
  const progFill= document.getElementById('cura-rprog-fill');
  const progWrap= document.getElementById('cura-rprog-wrap');
  const mImg    = document.getElementById('cura-rmodal-img');
  const mTitle  = document.getElementById('cura-rmodal-title');
  const mPrice  = document.getElementById('cura-rmodal-price');
  const mComp   = document.getElementById('cura-rmodal-compare');
  const mCta    = document.getElementById('cura-rmodal-cta');

  function openModal(card) {
    if (!modal || !vid) return;
    const src     = card.dataset.videoSrc || '';
    const poster  = card.dataset.videoPoster || '';
    const title   = card.dataset.title || '';
    const price   = card.dataset.price || '';
    const compare = card.dataset.compare || '';
    const url     = card.dataset.url || '#';
    const img     = card.dataset.img || '';

    vid.pause();
    vid.src = src;
    if (poster) vid.poster = poster;

    if (mImg)   { mImg.src = img; mImg.alt = title; }
    if (mTitle) mTitle.textContent = title;
    if (mPrice) mPrice.textContent = price;
    if (mComp)  { mComp.textContent = compare; mComp.style.display = compare ? '' : 'none'; }
    if (mCta)   mCta.href = url;

    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    if (src) {
      vid.load();
      vid.play().catch(() => {});
      setPlaying(true);
    }
  }

  function closeModal() {
    if (!modal) return;
    vid.pause();
    vid.src = '';
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    setPlaying(false);
    if (progFill) progFill.style.width = '0%';
  }

  function setPlaying(playing) {
    if (!playBtn) return;
    playBtn.querySelector('.i-play').style.display  = playing ? 'none' : '';
    playBtn.querySelector('.i-pause').style.display = playing ? '' : 'none';
  }

  if (modal) {
    modal.querySelectorAll('[data-close]').forEach(el => el.addEventListener('click', closeModal));
    document.addEventListener('keydown', e => { if (e.key === 'Escape') closeModal(); });
  }

  if (playBtn && vid) {
    playBtn.addEventListener('click', () => {
      if (vid.paused) { vid.play(); setPlaying(true); }
      else            { vid.pause(); setPlaying(false); }
    });
  }

  if (muteBtn && vid) {
    muteBtn.addEventListener('click', () => {
      vid.muted = !vid.muted;
      muteBtn.querySelector('.i-sound').style.display = vid.muted ? 'none' : '';
      muteBtn.querySelector('.i-muted').style.display = vid.muted ? '' : 'none';
    });
  }

  if (vid && progFill) {
    vid.addEventListener('timeupdate', () => {
      if (vid.duration) progFill.style.width = (vid.currentTime / vid.duration * 100) + '%';
    });
    vid.addEventListener('ended', () => setPlaying(false));
  }

  if (progWrap && vid) {
    progWrap.addEventListener('click', e => {
      const r = progWrap.getBoundingClientRect();
      vid.currentTime = ((e.clientX - r.left) / r.width) * vid.duration;
    });
  }

  /* ============================================================
     INIT
  ============================================================ */
  function init(scope) {
    const root = scope && scope.querySelectorAll ? scope : document;
    root.querySelectorAll('[data-reels-stage]').forEach(stage => {
      const section = stage.closest('.cura-reels');
      if (section && !section.dataset.reelsInit) {
        new Carousel(section);
        section.dataset.reelsInit = 'true';
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', e => init(e.target));

})();